/*
 * A published status, drawn the two ways the product draws one.
 *
 * Seven surfaces show a status and they want two different amounts of it. Where a person is *looked
 * up* — the profile column, the hover card, the head of a direct conversation, the account menu —
 * there is room for the whole of it, and the whole of it is the point: "In a meeting until 13:00"
 * answers the question the surface was opened with. Where a person is merely *listed* — a directory
 * card, a member row, a sidebar row — there is room for one glyph beside a name, and the words ride
 * along as the glyph's accessible name and its tooltip. So this file has a line and a mark, and no
 * surface decides for itself what a status looks like.
 *
 * Nothing about a status is decided here. What one *is* — the palette, the names of its emoji, what
 * it is called where only a name fits, how an end time reads — is `status.ts`, and this file is the
 * two pictures of it; the away claim is neither, and rides the presence dot (`presence.ts`).
 *
 * Both halves are drawn in the emoji face wherever an emoji stands in them: the interface face has
 * no colour glyphs, and one drawn in it is a tofu box (X-A1 D12, `ui/emojiText.tsx`). The glyph
 * wears `.tb-emoji` directly, because there is nothing to find in it — the whole string is the
 * emoji, and the server refuses any that is not on its palette. The line the person *wrote* is free
 * text of at most 64 characters, so an emoji may stand anywhere in it or nowhere at all, and it goes
 * through `emojiText` like every other written string this interface draws.
 */

import type { ReactNode } from "react"
import { emojiText } from "../../ui/index.ts"
import { useNow } from "../i18n/useNow.ts"
import "./people.css"
import { formatUntil, statusName, type Status } from "./status.ts"

/**
 * The glyph on its own, standing in for the whole status.
 *
 * The words are its accessible name and its tooltip, because in a list the glyph is all there is
 * room for and a picture is not an answer to a screen reader. A status with no line of its own is
 * named by its emoji (`statusName`), so the mark always says something a reader can act on and never
 * leans on whatever name the reader's screen reader happens to give an emoji.
 */
export function StatusMark({ status }: { status: Status | null | undefined }): ReactNode {
	if (status === null || status === undefined || status.emoji === "") return null
	const words = statusName(status)
	return (
		<span className="tb-emoji tb-status-mark" role="img" aria-label={words} title={words}>
			{status.emoji}
		</span>
	)
}

/**
 * The whole status as one quiet line: the glyph, what the person wrote, and when it clears.
 *
 * The glyph is hidden from a screen reader wherever the words stand beside it — they are the same
 * statement, and an emoji's own name read out in front of the sentence it decorates is noise. Where
 * there are no words it speaks, because then it is the whole statement.
 */
export function StatusLine({ status }: { status: Status | null | undefined }): ReactNode {
	// The end time ages while somebody looks at it, and it is the half of the line that stops being
	// true: the interface's one clock ticks it with every other stamp on the screen.
	const now = useNow()
	if (status === null || status === undefined) return null
	// `statusOf` is the only thing that makes one of these and it answers null for a person who
	// published nothing, so there is always an emoji or a line below and never an empty row.
	const alone = status.text === ""
	const until = formatUntil(status.until, now)
	return (
		<p className="tb-status-line">
			{status.emoji === "" ? null : (
				<span
					className="tb-emoji tb-status-mark"
					{...(alone ? { role: "img", "aria-label": statusName(status) } : { "aria-hidden": true })}
				>
					{status.emoji}
				</span>
			)}
			{alone ? null : <span className="tb-status-line__words">{emojiText(status.text)}</span>}
			{until === "" ? null : <span className="tb-status-line__until">{until}</span>}
		</p>
	)
}
